import React, { useRef, useState } from 'react';
import axios from 'axios';
import { Car, MapPin, Calendar, Clock, Users, Loader2, CheckCircle2, ArrowRight, Search, X, Building2, Star, CreditCard, ShieldCheck } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from '../hooks/use-toast';
import Disclaimer from '../components/Disclaimer';
import { useSettings } from '../context/SettingsContext';
import { usePageMeta } from '../hooks/usePageMeta';

import { API } from '../config';

const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

/* ---------- Place autocomplete (OpenStreetMap geocode via backend) ---------- */
const PlaceInput = ({ icon: Icon, placeholder, value, onSelect, testid }) => {
  const [q, setQ] = useState(value?.text || '');
  const [opts, setOpts] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);

  const onChange = (text) => {
    setQ(text);
    onSelect(null);
    clearTimeout(timer.current);
    if (text.trim().length < 3) { setOpts([]); setOpen(false); return; }
    setLoading(true);
    timer.current = setTimeout(async () => {
      try {
        const r = await axios.post(`${API}/cabs/geocode`, { q: text });
        setOpts(r.data.results || []);
        setOpen(true);
      } catch (_) { setOpts([]); } finally { setLoading(false); }
    }, 450);
  };

  const pick = (o) => {
    onSelect({ text: o.display_name, lat: o.lat, lon: o.lon });
    setQ(o.display_name);
    setOpen(false);
  };

  return (
    <div className="relative">
      <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-xl px-3 py-2 focus-within:border-tp-violet transition-colors">
        <Icon size={18} className="text-tp-violet shrink-0" />
        <input
          data-testid={testid}
          value={q}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => opts.length && setOpen(true)}
          placeholder={placeholder}
          className="w-full outline-none text-sm bg-transparent"
        />
        {loading && <Loader2 size={15} className="animate-spin text-gray-400" />}
        {q && !loading && (
          <button onClick={() => { setQ(''); onSelect(null); setOpts([]); }} className="text-gray-400 hover:text-gray-600"><X size={15} /></button>
        )}
      </div>
      {open && opts.length > 0 && (
        <div className="absolute z-30 mt-1 w-full bg-white border border-gray-100 rounded-xl card-shadow max-h-64 overflow-auto" data-testid={`${testid}-suggestions`}>
          {opts.map((o, i) => (
            <button
              key={i}
              onClick={() => pick(o)}
              className="w-full text-left px-3 py-2 text-xs hover:bg-tp-violet-soft border-b border-gray-50 last:border-0 flex items-start gap-2"
            >
              <MapPin size={13} className="text-tp-coral mt-0.5 shrink-0" />
              <span className="text-tp-ink">{o.display_name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/* ---------- parse MYF search into bookable car options ----------
 * Fares live in masters.rate_list[fareId].estimated_fare = {categoryId: amount}.
 * Vendors (when present) live at top-level `vendors` (NOT inside masters); each
 * vendor.rates[] maps fare_id + category_id, so we attribute a vendor per option.
 */
function buildOptions(masters, vendors, categoryFilter) {
  const cats = masters.car_category_list || {};
  const rates = masters.rate_list || {};
  const vendorByKey = {};
  Object.values(vendors || {}).forEach((v) => {
    (v.rates || []).forEach((r) => { vendorByKey[`${r.fare_id}-${r.category_id}`] = v; });
  });
  const out = [];
  Object.entries(rates).forEach(([fareId, fare]) => {
    const est = fare.estimated_fare || {};
    const taxes = fare.payable_tax || {};
    Object.entries(est).forEach(([catId, amount]) => {
      const cat = cats[catId];
      if (!cat) return;
      if (categoryFilter && categoryFilter.length && !categoryFilter.includes(String(catId))) return;
      const v = vendorByKey[`${fareId}-${catId}`];
      const tax = Number(taxes[catId] || 0);
      out.push({
        key: `${fareId}-${catId}`,
        fare_id: fareId,
        package_id: fareId,
        category_id: String(catId),
        category_name: cat.category_name,
        model_names: cat.model_names,
        seats: cat.max_allowed_people,
        has_ac: cat.has_ac,
        service_type: fare.service_type,
        package_type: fare.package_type,
        included_distance: fare.included_distance,
        included_time: fare.included_time,
        distance_unit: fare.distance_unit,
        currency: fare.currency || 'INR',
        base_fare: Math.round(Number(amount)),
        tax: Math.round(tax),
        fare: Math.round(Number(amount) + tax),
        vendor_id: v?.vendor_id || '',
        vendor_name: v?.vendor_name || 'Assigned partner',
        vendor_number: v?.vendor_number || '',
        vendor_rating: v?.vendor_rating && v.vendor_rating !== '--' ? v.vendor_rating : null,
      });
    });
  });
  return out.sort((a, b) => a.fare - b.fare);
}

const todayStr = () => new Date().toISOString().slice(0, 10);

// Default pickup ~1h ahead (rounded to next hour) so the first search isn't
// rejected by MYF for being in the past.
const nextHour = () => {
  const d = new Date(Date.now() + 60 * 60 * 1000);
  d.setMinutes(0, 0, 0);
  return d;
};

const CAB_FAQS = [
  {
    q: 'How can I book a cab to my railway station?',
    a: 'Visit the TrainPanda Cabs page, enter the required pickup and destination details, review the available cab options, and follow the booking instructions to confirm your ride.'
  },
  {
    q: 'What are Railway Station Pick Drop Cab Booking services?',
    a: 'These services help passengers arrange transportation to or from a railway station. They can be useful when travelling from home to the station or when arranging a ride after arriving at your destination.'
  },
  {
    q: 'Does TrainPanda offer railway station pickup and drop services?',
    a: 'TrainPanda provides a page to explore railway station cab booking options. Available pickup and drop services depend on your location, route, and the packages offered at the time of booking.'
  },
  {
    q: 'Can I pay for my cab booking online?',
    a: 'Payment terms depend on the selected booking. The service may offer advance online payment with the remaining amount payable to the driver. Review the payment conditions displayed during booking.'
  },
  {
    q: 'What is a railway station transfer taxi?',
    a: 'A railway station transfer taxi is a cab arranged to transport passengers between a railway station and another location, such as their home, hotel, or office. Check TrainPanda for available options and applicable fares.'
  }
];

const CabBooking = () => {
  usePageMeta(
    'Railway Station Cab Booking – Pick Drop Booking | TrainPanda',
    'Book a cab for railway station pickup and drop with TrainPanda. Enjoy convenient, reliable and hassle-free station transfer services.'
  );
  const settings = useSettings();
  const cabSettings = settings?.myf_cabs || {};
  const advanceEnabled = Boolean(cabSettings.advance_enabled);
  const advanceType = cabSettings.advance_type || 'fixed';
  const advanceValue = Number(cabSettings.advance_value ?? 100);
  const razorpayKey = settings?.payment?.razorpay_key_id;

  const [from, setFrom] = useState(null);
  const [date, setDate] = useState(() => nextHour().toISOString().slice(0, 10));
  const [time, setTime] = useState(() => {
    const d = nextHour();
    return `${String(d.getHours()).padStart(2, '0')}:00`;
  });
  const serviceType = 'local-package'; // only Local Package for now
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState(null);
  const [meta, setMeta] = useState(null);
  const [selected, setSelected] = useState(null);
  const [booking, setBooking] = useState(false);
  const [confirmed, setConfirmed] = useState(null);
  const [traveler, setTraveler] = useState({ name: '', email: '', mobile: '' });
  const [openFaq, setOpenFaq] = useState(null);

  const getAdvanceAmount = (fare) => {
    if (!advanceEnabled) return 0;
    if (advanceType === 'full') return fare;
    if (advanceType === 'percentage') {
      const pct = Math.max(1, Math.min(100, advanceValue));
      return Math.max(1, Math.round((fare * pct) / 100));
    }
    return Math.max(1, Math.min(fare, Math.round(advanceValue)));
  };

  const doSearch = async () => {
    if (!from) { toast({ title: 'Enter a pickup location' }); return; }
    setLoading(true); setOptions(null); setSelected(null); setMeta(null);
    try {
      const body = {
        pickup_date: date,
        pickup_time: `${time}:00`,
        pickup_lat: from.lat,
        pickup_long: from.lon,
        service_type: serviceType,
      };
      const r = await axios.post(`${API}/cabs/search`, body);
      const opts = buildOptions(r.data.masters || {}, r.data.vendors || {}, r.data.category_ids || []);
      setOptions(opts);
      setMeta({
        request_id: r.data.request_id,
        request_time: r.data.request_time,
        vendor_count: Object.keys(r.data.vendors || {}).length,
        vendors: Object.values(r.data.vendors || {}),
      });
      if (!opts.length) toast({ title: 'No cabs found for this time', description: 'Try a different location or date.' });
    } catch (e) {
      toast({ title: 'Cab search failed', description: e.response?.data?.detail || 'Please try again.' });
      setOptions([]);
    } finally { setLoading(false); }
  };

  const startBooking = async () => {
    if (!traveler.name || !traveler.email || !/^\d{10}$/.test(traveler.mobile)) {
      toast({ title: 'Please fill name, email and a valid 10-digit mobile' });
      return;
    }
    const advAmount = getAdvanceAmount(selected.fare);

    const buildBookingPayload = (paymentInfo = {}) => ({
      traveler_name: traveler.name,
      traveler_email_id: traveler.email,
      traveler_mobile_no: traveler.mobile,
      service_type: selected.service_type,
      category_id: selected.category_id,
      category_name: selected.category_name,
      vendor_id: selected.vendor_id,
      vendor_name: selected.vendor_name,
      package_id: String(selected.package_id),
      pickup_datetime: `${date} ${time}:00`,
      pickup_area: from.text,
      pickup_area_latitude: from.lat,
      pickup_area_longitude: from.lon,
      pickup_address: from.text,
      fare_amount: selected.fare,
      ...paymentInfo,
    });

    if (advanceEnabled && advAmount > 0) {
      setBooking(true);
      try {
        const orderRes = await axios.post(`${API}/cabs/payment/order`, {
          amount: advAmount,
          traveler_name: traveler.name,
          traveler_email: traveler.email,
          traveler_phone: traveler.mobile,
        });

        const loaded = await loadRazorpayScript();
        if (!loaded) {
          toast({ title: 'Payment Error', description: 'Could not load payment gateway. Please try again.' });
          setBooking(false);
          return;
        }

        const options = {
          key: orderRes.data.key_id || razorpayKey,
          amount: orderRes.data.amount,
          currency: orderRes.data.currency || 'INR',
          name: 'TrainPanda Cabs',
          description: `Advance for ${selected.category_name} (${selected.vendor_name})`,
          order_id: orderRes.data.order_id,
          prefill: {
            name: traveler.name,
            email: traveler.email,
            contact: traveler.mobile,
          },
          theme: { color: '#6C5CE7' },
          handler: async function (response) {
            try {
              setBooking(true);
              const r = await axios.post(`${API}/cabs/book`, buildBookingPayload({
                advance_amount: advAmount,
                payment_mode: 'upi',
                payment_type: advAmount >= selected.fare ? 'full' : 'partial',
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
              }));
              setConfirmed(r.data);
              setSelected(null);
            } catch (err) {
              const d = err.response?.data?.detail;
              toast({ title: 'Booking failed', description: (typeof d === 'object' ? d.message : d) || 'Please try again.' });
            } finally {
              setBooking(false);
            }
          },
          modal: {
            ondismiss: function () {
              setBooking(false);
            },
          },
        };

        const rzp = new window.Razorpay(options);
        rzp.on('payment.failed', function (resp) {
          setBooking(false);
          toast({ title: 'Payment failed', description: resp.error?.description || 'Payment was unsuccessful' });
        });
        rzp.open();
      } catch (err) {
        setBooking(false);
        const d = err.response?.data?.detail;
        toast({ title: 'Payment initialization failed', description: (typeof d === 'object' ? d.message : d) || 'Please try again.' });
      }
    } else {
      setBooking(true);
      try {
        const r = await axios.post(`${API}/cabs/book`, buildBookingPayload());
        setConfirmed(r.data);
        setSelected(null);
      } catch (err) {
        const d = err.response?.data?.detail;
        toast({ title: 'Booking failed', description: (typeof d === 'object' ? d.message : d) || 'Please try again.' });
      } finally {
        setBooking(false);
      }
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-4 md:py-5">
      {/* Header */}
      <div className="bnr-violet rounded-3xl p-6 md:p-8 mb-6 relative overflow-hidden">
        <div className="relative z-10">
          <div className="inline-flex items-center gap-1.5 bg-white/20 rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wider mb-2"><Car size={13} /> Cabs · Local Package</div>
          <h1 className="text-3xl md:text-4xl font-black leading-tight">Book a cab to your station</h1>
          <p className="text-white/80 text-sm mt-1">
            {advanceEnabled
              ? 'Door-to-door local packages. Pay advance online & rest to driver.'
              : 'Door-to-door local packages. Pay the driver directly — no advance payment.'}
          </p>
        </div>
        <Car size={140} className="absolute -right-6 -bottom-8 text-white/10" />
      </div>

      {/* Search card */}
      <div className="bg-white rounded-2xl border border-gray-100 card-shadow p-4 md:p-6 space-y-4">
        <div>
          <label className="text-[11px] font-bold uppercase text-gray-500 tracking-wide">Pickup location</label>
          <PlaceInput icon={MapPin} placeholder="Where from?" value={from} onSelect={setFrom} testid="cab-from" />
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <div>
            <label className="text-[11px] font-bold uppercase text-gray-500 tracking-wide flex items-center gap-1"><Calendar size={12} /> Date</label>
            <Input type="date" min={todayStr()} value={date} onChange={(e) => setDate(e.target.value)} className="mt-1" data-testid="cab-date" />
          </div>
          <div>
            <label className="text-[11px] font-bold uppercase text-gray-500 tracking-wide flex items-center gap-1"><Clock size={12} /> Time</label>
            <Input type="time" value={time} onChange={(e) => setTime(e.target.value)} className="mt-1" data-testid="cab-time" />
          </div>
          <div>
            <label className="text-[11px] font-bold uppercase text-gray-500 tracking-wide">Service</label>
            <div className="mt-1 h-10 flex items-center px-3 rounded-md border border-gray-200 bg-gray-50 text-sm font-semibold text-tp-violet-deep" data-testid="cab-service">
              Local Package
            </div>
          </div>
        </div>

        <Button onClick={doSearch} disabled={loading} className="btn-violet w-full text-white font-bold py-3 rounded-xl" data-testid="cab-search-btn">
          {loading ? <Loader2 className="animate-spin mr-2" size={18} /> : <Search size={18} className="mr-2" />}
          Search Cabs
        </Button>
      </div>

      {/* Results */}
      {options && (
        <div className="mt-6 space-y-3" data-testid="cab-results">
          {/* Request metadata bar */}
          {meta && (options.length > 0 || meta.request_id) && (
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-gray-500 bg-white rounded-xl border border-gray-100 px-3 py-2" data-testid="cab-search-meta">
              <span className="font-bold text-tp-ink">{options.length} option{options.length !== 1 ? 's' : ''}</span>
              {meta.vendor_count > 0 && <span className="flex items-center gap-1"><Building2 size={12} /> {meta.vendor_count} vendor{meta.vendor_count !== 1 ? 's' : ''}</span>}
              {meta.request_id && <span>Request ID: <b className="text-tp-ink" data-testid="cab-request-id">{meta.request_id}</b></span>}
              {meta.request_time && <span className="flex items-center gap-1"><Clock size={11} /> {meta.request_time}</span>}
            </div>
          )}
          {options.length === 0 && !loading && (
            <div className="text-center py-12 text-gray-500 bg-white rounded-2xl border border-gray-100">
              <Car size={40} className="mx-auto mb-3 text-gray-300" />
              No cabs available for this time. Try a different location or date.
            </div>
          )}
          {options.map((o) => (
            <div key={o.key} className="bg-white rounded-2xl border border-gray-100 card-shadow p-4 flex items-center gap-4" data-testid="cab-option">
              <div className="w-14 h-14 rounded-2xl bg-tp-violet-soft flex items-center justify-center text-tp-violet-deep shrink-0">
                <Car size={26} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-black text-tp-ink">{o.category_name} {o.has_ac === 'yes' && <span className="text-[10px] font-bold bg-tp-lime-soft text-tp-lime-deep px-1.5 py-0.5 rounded ml-1">AC</span>}</div>
                <div className="text-[11px] text-gray-500 truncate">{o.model_names || 'Assorted models'}</div>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-gray-500 mt-1">
                  <span className="flex items-center gap-1"><Users size={12} /> {o.seats} seats</span>
                  {o.included_distance ? <span>{o.included_distance} {o.distance_unit || 'km'} incl.</span> : null}
                  {o.included_time ? <span>{Math.round(o.included_time / 60)}h incl.</span> : null}
                </div>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] mt-1">
                  <span className="flex items-center gap-1 text-tp-violet-deep font-semibold"><Building2 size={12} /> {o.vendor_name}</span>
                  {o.vendor_rating && <span className="flex items-center gap-0.5 text-amber-600"><Star size={11} fill="currentColor" /> {o.vendor_rating}</span>}
                  {o.vendor_number && <span className="text-gray-400">{o.vendor_number}</span>}
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-xl font-black text-tp-ink">₹{o.fare}</div>
                <div className="text-[10px] text-gray-400">base ₹{o.base_fare} + tax ₹{o.tax}</div>
                <Button onClick={() => { setSelected(o); }} className="btn-primary text-white text-xs font-bold mt-1 rounded-lg px-4" data-testid={`cab-book-${o.category_id}`}>
                  Book <ArrowRight size={13} className="ml-1" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Traveler / confirm dialog */}
      <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <DialogContent className="max-w-md" data-testid="cab-traveler-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Car size={18} className="text-tp-violet" /> Confirm your ride</DialogTitle></DialogHeader>
          {selected && (() => {
            const advAmount = getAdvanceAmount(selected.fare);
            const remAmount = Math.max(0, selected.fare - advAmount);
            return (
              <div className="space-y-3">
                <div className="bg-tp-violet-soft rounded-xl p-3 text-sm">
                  <div className="font-bold text-tp-ink">{selected.category_name} · {selected.vendor_name}</div>
                  <div className="text-xs text-gray-600 mt-0.5">Pickup: {from?.text?.split(',').slice(0, 2).join(', ')}</div>
                  <div className="text-xs text-gray-600">{date} at {time}</div>
                  <div className="text-lg font-black text-tp-violet-deep mt-1">₹{selected.fare} <span className="text-[11px] font-normal text-gray-500">total fare</span></div>
                </div>

                {advanceEnabled && advAmount > 0 && (
                  <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-3 text-xs space-y-1.5">
                    <div className="flex justify-between items-center text-emerald-900 font-bold">
                      <span className="flex items-center gap-1.5"><CreditCard size={13} className="text-emerald-600" /> Advance Online</span>
                      <span>₹{advAmount}</span>
                    </div>
                    {remAmount > 0 && (
                      <div className="flex justify-between items-center text-gray-600 text-[11px] pt-1 border-t border-emerald-100/60">
                        <span>Pay to driver at drop</span>
                        <span className="font-semibold text-tp-ink">₹{remAmount}</span>
                      </div>
                    )}
                  </div>
                )}

                <Input placeholder="Full name" value={traveler.name} onChange={(e) => setTraveler({ ...traveler, name: e.target.value })} data-testid="cab-traveler-name" />
                <Input placeholder="Email" type="email" value={traveler.email} onChange={(e) => setTraveler({ ...traveler, email: e.target.value })} data-testid="cab-traveler-email" />
                <Input placeholder="10-digit mobile" maxLength={10} value={traveler.mobile} onChange={(e) => setTraveler({ ...traveler, mobile: e.target.value.replace(/\D/g, '') })} data-testid="cab-traveler-mobile" />
                
                <Button onClick={startBooking} disabled={booking} className="btn-violet w-full text-white font-bold py-3 rounded-xl" data-testid="cab-pay-btn">
                  {booking ? <Loader2 className="animate-spin mr-2" size={18} /> : (advanceEnabled && advAmount > 0 ? <CreditCard className="mr-2" size={17} /> : null)}
                  {advanceEnabled && advAmount > 0 ? `Pay ₹${advAmount} & Book Cab` : 'Confirm Booking'}
                </Button>
                <p className="text-[10px] text-gray-400 text-center">
                  {advanceEnabled && advAmount > 0
                    ? 'Secure online payment. Instant booking reference from provider.'
                    : 'No advance payment — pay the driver directly. You get a booking reference instantly.'}
                </p>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Confirmation dialog */}
      <Dialog open={!!confirmed} onOpenChange={(v) => !v && setConfirmed(null)}>
        <DialogContent className="max-w-sm" data-testid="cab-confirmed-dialog">
          <div className="py-2">
            <div className="text-center">
              <CheckCircle2 size={52} className="mx-auto text-tp-lime-deep mb-2" />
              <h3 className="text-xl font-black text-tp-ink">Cab booked!</h3>
              <p className="text-xs text-gray-500 mt-1">
                {confirmed?.remaining_amount > 0
                  ? `Your ride is confirmed. Pay ₹${confirmed.remaining_amount} to driver at drop.`
                  : 'Your ride is confirmed.'}
              </p>
            </div>
            {confirmed && (
              <div className="mt-3 bg-gray-50 rounded-xl p-3 text-sm space-y-1.5">
                {confirmed.booking_reference_number && (
                  <div className="flex justify-between"><span className="text-gray-500">Booking Ref</span><b className="text-tp-ink" data-testid="cab-confirmed-ref">{confirmed.booking_reference_number}</b></div>
                )}
                {confirmed.trn && <div className="flex justify-between"><span className="text-gray-500">TRN</span><span className="text-tp-ink">{confirmed.trn}</span></div>}
                {confirmed.category_name && <div className="flex justify-between"><span className="text-gray-500">Cab</span><span className="text-tp-ink">{confirmed.category_name}</span></div>}
                {confirmed.vendor_name && <div className="flex justify-between"><span className="text-gray-500">Vendor</span><span className="text-tp-ink">{confirmed.vendor_name}</span></div>}
                {confirmed.pickup_datetime && <div className="flex justify-between"><span className="text-gray-500">Pickup</span><span className="text-tp-ink">{confirmed.pickup_datetime}</span></div>}
                {confirmed.fare_amount != null && <div className="flex justify-between"><span className="text-gray-500">Total Fare</span><b className="text-tp-violet-deep">₹{confirmed.fare_amount}</b></div>}
                {confirmed.advance_amount > 0 && (
                  <div className="flex justify-between text-emerald-700 font-semibold"><span className="flex items-center gap-1"><CheckCircle2 size={13} /> Advance Paid</span><span>₹{confirmed.advance_amount}</span></div>
                )}
                {confirmed.remaining_amount > 0 && (
                  <div className="flex justify-between text-gray-600 text-xs"><span>Pay to Driver</span><span className="font-semibold text-tp-ink">₹{confirmed.remaining_amount}</span></div>
                )}
              </div>
            )}
            <Button onClick={() => setConfirmed(null)} className="btn-violet text-white font-bold mt-4 w-full rounded-xl" data-testid="cab-confirmed-done">Done</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* SEO & FAQ Content Section from Drive */}
      <section className="mt-12 bg-white rounded-3xl p-6 sm:p-10 border border-gray-100 shadow-sm space-y-8">
        <div>
          <h3 className="text-2xl sm:text-3xl font-extrabold text-[#0B2E5C] tracking-tight">
            Book a Cab to Your Station with TrainPanda
          </h3>
          <p className="text-sm sm:text-base text-gray-600 leading-relaxed mt-3">
            Planning a train journey? Make your travel easier with TrainPanda's convenient cab booking services. Whether you need a ride to the railway station or a pickup after reaching your destination, TrainPanda helps you plan your station transfers with greater convenience. With options for local travel and door-to-door packages, you can arrange transportation according to your journey requirements.
          </p>
          <p className="text-sm sm:text-base text-gray-600 leading-relaxed mt-2">
            Book a cab to your station and reduce the stress of arranging last-minute transportation. TrainPanda offers a convenient way to explore railway station pickup and drop services, helping you connect your home, hotel, office, or other location with the railway station.
          </p>
          <p className="text-sm sm:text-base text-gray-600 leading-relaxed mt-2">
            Visit <a href="https://www.trainpanda.com/cabs" className="text-tp-violet font-semibold underline">TrainPanda Cabs</a> to explore available cab booking options for your next journey.
          </p>
        </div>

        <div className="pt-4 border-t border-gray-100 space-y-3">
          <h4 className="text-lg sm:text-xl font-bold text-[#0B2E5C]">
            Railway Station Pick Drop Cab Booking
          </h4>
          <p className="text-sm text-gray-600 leading-relaxed">
            Getting to the railway station on time is an important part of a smooth travel experience. TrainPanda makes it convenient to explore Railway Station Pick Drop Cab Booking options for your travel plans. Whether you are travelling alone, with family, or carrying luggage, booking a cab can help make your station transfer more comfortable.
          </p>
          <p className="text-sm text-gray-600 leading-relaxed">
            You can plan your pickup according to your train schedule and choose a suitable journey option based on availability. Advance planning is particularly helpful when travelling early in the morning, late at night, or during busy travel periods.
          </p>
          <p className="text-sm text-gray-600 leading-relaxed">
            With available local packages, passengers can explore cab options for different transportation needs. Check the fare, pickup location, destination, and booking terms before confirming your ride.
          </p>
        </div>

        <div className="pt-4 border-t border-gray-100 space-y-3">
          <h4 className="text-lg sm:text-xl font-bold text-[#0B2E5C]">
            Railway Station Transfer Taxi for Comfortable Travel
          </h4>
          <p className="text-sm text-gray-600 leading-relaxed">
            Looking for a convenient Railway Station Transfer Taxi? TrainPanda helps you explore cab booking options for transfers between railway stations and nearby destinations.
          </p>
          <p className="text-sm text-gray-600 leading-relaxed">
            Instead of managing multiple transportation connections, you can look for a direct ride that suits your travel plans. Depending on availability, local cab packages may be suitable for station transfers and other nearby journeys.
          </p>
          <p className="text-sm text-gray-600 leading-relaxed">
            Before booking, review the vehicle details, fare, included distance or duration, and any additional charges. This helps you understand the package and plan your transportation budget more effectively.
          </p>
        </div>

        <div className="pt-4 border-t border-gray-100 space-y-3">
          <h4 className="text-lg sm:text-xl font-bold text-[#0B2E5C]">
            Easy Cab Booking and Convenient Payment Options
          </h4>
          <p className="text-sm text-gray-600 leading-relaxed">
            TrainPanda provides a convenient starting point for arranging station transportation. The available booking process may include advance online payment, with the remaining amount payable to the driver according to the booking terms.
          </p>
          <p className="text-sm text-gray-600">
            Follow these steps to get started:
          </p>
          <ol className="list-decimal list-inside text-sm text-gray-600 space-y-1.5 pl-1">
            <li>Visit <a href="https://www.trainpanda.com/cabs" className="underline text-tp-violet font-medium">https://www.trainpanda.com/cabs</a></li>
            <li>Enter your pickup and drop-off details as requested.</li>
            <li>Explore the available cab packages.</li>
            <li>Review the fare, payment terms, and journey details.</li>
            <li>Confirm your booking according to the instructions provided.</li>
          </ol>
          <p className="text-sm text-gray-600 leading-relaxed mt-2">
            Always check your booking confirmation and payment conditions before travelling.
          </p>
        </div>

        <div className="pt-4 border-t border-gray-100 space-y-3">
          <h4 className="text-lg sm:text-xl font-bold text-[#0B2E5C]">
            Why Choose TrainPanda for Railway Station Cab Booking?
          </h4>
          <p className="text-sm text-gray-600">
            TrainPanda brings useful travel services together to help passengers plan their journeys more conveniently. With available cab booking options, you can explore transportation for station pickups, station drops, and local travel.
          </p>
          <ul className="list-disc list-inside text-sm text-gray-600 space-y-1.5 pl-1">
            <li><strong className="text-gray-800">Convenient planning:</strong> Arrange your station transfer ahead of time.</li>
            <li><strong className="text-gray-800">Pickup and drop options:</strong> Explore rides suited to your journey.</li>
            <li><strong className="text-gray-800">Local packages:</strong> Check available packages for your travel needs.</li>
            <li><strong className="text-gray-800">Payment flexibility:</strong> Review the advance payment and driver payment terms.</li>
            <li><strong className="text-gray-800">Simple access:</strong> Visit the website from your mobile phone or computer.</li>
          </ul>
          <p className="text-sm text-gray-600 leading-relaxed mt-2">
            Cab availability, pricing, vehicle options, and package conditions may vary by location and booking. Check the latest details before confirming your ride.
          </p>
        </div>

        <div className="pt-4 border-t border-gray-100 space-y-3">
          <h4 className="text-lg sm:text-xl font-bold text-[#0B2E5C]">
            Book Your Railway Station Cab with TrainPanda
          </h4>
          <p className="text-sm text-gray-600 leading-relaxed">
            Make your next train journey more convenient by planning your transportation in advance. Whether you need a cab to catch your train or a ride after reaching your destination, TrainPanda helps you explore railway station transfer options.
          </p>
          <p className="text-sm font-semibold text-tp-violet">
            Book your cab today: <a href="https://www.trainpanda.com/cabs" className="underline hover:text-tp-violet/80">https://www.trainpanda.com/cabs</a>
          </p>
        </div>

        {/* FAQ Section */}
        <div className="pt-6 border-t border-gray-200">
          <h4 className="text-xl font-bold text-[#0B2E5C] mb-4">
            Frequently Asked Questions (FAQs)
          </h4>
          <div className="space-y-3">
            {CAB_FAQS.map((faq, idx) => (
              <div key={idx} className="border border-gray-200/80 rounded-2xl overflow-hidden bg-white">
                <button
                  type="button"
                  onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                  className="w-full px-5 py-4 text-left font-bold text-gray-800 flex items-center justify-between gap-4 hover:text-tp-violet transition-colors"
                >
                  <span>{idx + 1}. {faq.q}</span>
                  <span className="text-xl text-gray-400 font-light select-none">
                    {openFaq === idx ? '−' : '+'}
                  </span>
                </button>
                {openFaq === idx && (
                  <div className="px-5 pb-4 text-sm text-gray-600 leading-relaxed border-t border-gray-100 pt-3">
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="mt-8"><Disclaimer /></div>
    </div>
  );
};

export default CabBooking;
